<template>
  <div v-if="!isMinimized" class="ai-prompt-wrapper">
    <div
      ref="wrapperRef"
      class="ai-floating-chat"
      :class="{ open: show }"
      :style="wrapperStyle"
    >
      <!-- Fumetto esterno: visibile finché non apri la chat -->
      <transition name="ai-greet">
        <div
          v-if="showGreeting"
          :class="['greeting-bubble', greetSide]"
          role="status"
          aria-live="polite"
        >
          <p>{{ greetingText }}</p>
        </div>
      </transition>

      <!-- FAB draggable -->
      <div class="fab-container" :class="{ dragging: isDragging }">
        <button
          ref="fabRef"
          class="ai-toggle-btn"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerCancel"
          aria-label="Apri assistente AI"
          title="Trascina per spostare • Clic per aprire"
        >
          <img src="../assets/airobot.png" alt="Ai Icon" />
        </button>
        
        <!-- Pulsante X sul FAB per minimizzare direttamente -->
        <button 
          v-if="!show"
          class="fab-close-btn" 
          @click.stop="minimizeChat"
          title="Minimizza assistente"
        >
          <XIcon />
        </button>
      </div>

      <!-- Prompt box -->
      <div
        v-if="show"
        ref="promptBoxRef"
        class="ai-prompt-box"
        :class="dropBelow ? 'below' : 'above'"
      >
        <button
          class="ai-close-btn"
          @click="closeButtonPrompt()"
          aria-label="Chiudi"
          title="Chiudi"
        >
          <XIcon />
        </button>

        <textarea
          v-model="prompt"
          placeholder="Scrivi il tuo comando…"
          @keydown.enter.exact.prevent="sendPrompt"
          @keydown.meta.enter.prevent="sendPrompt"
          @keydown.ctrl.enter.prevent="sendPrompt"
          @keydown.esc.prevent.stop="show = false"
        />

        <div class="chips">
          <span class="chips-label">Esempi rapidi</span>
          <button
            v-for="(s, i) in samples"
            :key="i"
            class="chip"
            :title="s.tip"
            @click="useSample(s.text, $event)"
          >
            {{ s.label }}
          </button>
        </div>

        <button class="send-btn" @click="sendPrompt">Invia</button>
      </div>
    </div>
  </div>

  <!-- Tab in basso a destra quando è minimizzato -->
  <button 
    v-else 
    class="ai-chat-tab" 
    @click="restoreChat"
    title="Apri assistente AI"
  >
    <div class="tab-icon">
      <img src="../assets/airobot.png" alt="Ai Icon" />
    </div>
    <span>Assistente AI</span>
  </button>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import { X as XIcon } from "lucide-vue-next";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useMapContext } from '@/composables/useMapContext'; // [NEW] Map Context for AI

/* ---------- Prop: username suggerito da App.vue ---------- */
const props = defineProps<{ userName?: string }>();

/* ---------- DRAG state ---------- */
const FAB_SIZE = 56,
  MARGIN = 12,
  CLICK_TOLERANCE = 5;
const PERSIST_PER_ROUTE = false;

const wrapperRef = ref<HTMLElement | null>(null);
const fabRef = ref<HTMLButtonElement | null>(null);
const promptBoxRef = ref<HTMLElement | null>(null);

const pos = reactive({ x: 0, y: 0 });
const wrapperStyle = computed(() => ({
  left: `${pos.x}px`,
  top: `${pos.y}px`,
}));

const isDragging = ref(false);
let startPointer = { x: 0, y: 0 },
  startPos = { x: 0, y: 0 },
  movedDistance = 0;

const route = useRoute();
const router = useRouter();
const storageKey = computed(
  () => `aiPromptPos:${String(route.name ?? "default")}`
);

// [NEW] Map Context for AI
const { hasContext, getContextForAI, contextSummary } = useMapContext();

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}
function viewportMaxX() {
  return Math.max(0, window.innerWidth - FAB_SIZE - MARGIN);
}
function viewportMaxY() {
  return Math.max(0, window.innerHeight - FAB_SIZE - MARGIN);
}
function defaultPos() {
  return {
    x: clamp(window.innerWidth - FAB_SIZE - 20, MARGIN, viewportMaxX()),
    y: clamp(window.innerHeight - FAB_SIZE - 20, MARGIN, viewportMaxY()),
  };
}
function loadPos() {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) {
      const p = JSON.parse(raw);
      pos.x = clamp(+p.x || 0, MARGIN, viewportMaxX());
      pos.y = clamp(+p.y || 0, MARGIN, viewportMaxY());
      return;
    }
  } catch {}
  const d = defaultPos();
  pos.x = d.x;
  pos.y = d.y;
}
function savePos() {
  try {
    localStorage.setItem(
      storageKey.value,
      JSON.stringify({ x: pos.x, y: pos.y })
    );
  } catch {}
}
function resetPos() {
  const d = defaultPos();
  pos.x = d.x;
  pos.y = d.y;
  try {
    localStorage.removeItem(storageKey.value);
  } catch {}
}

/* ---------- Stato chat ---------- */
const show = ref(false);
const isMinimized = ref(localStorage.getItem("ai-chat-minimized") === "true");
const prompt = ref("");
const toast = useToast();

function minimizeChat() {
  isMinimized.value = true;
  show.value = false;
  localStorage.setItem("ai-chat-minimized", "true");
}

function restoreChat() {
  isMinimized.value = false;
  resetPos();
  updateGreetSide();
  localStorage.setItem("ai-chat-minimized", "false");
}

/* Toast helpers */
const ok = (msg: string) =>
  toast.success(msg, { position: "top-right", duration: 2200 });

const successByIntent: Record<string, string> = {
  addNewDevice: "Device creato con successo",
  addNewNode: "Modulo aggiunto",
  createConnection: "Connessione creata",
  moveNode: "Modulo spostato",
  removeNode: "Modulo rimosso",
  other: "Operazione eseguita",
  dashboard: "Ecco la dashboard",
  devices: "Ecco la lista dispositivi",
  data: "Ecco la sezione dati",
  account: "Ecco la pagina account",
};

const samples = ref([
  {
    label: "MB→SAT→LTE",
    text: "Crea un nuovo device con MB → SAT → LTE/5G",
    tip: "Crea catena",
  },
  {
    label: "Aggiungi modulo con coordinate default",
    text: "Sul device (id) aggiungi un modulo (label) con coordinate default",
    tip: "Aggiungi nodo con coordinate default",
  },
  {
    label: "Aggiungi modulo con determinata posizione",
    text: "Sul device (id) aggiungi un modulo (label) alle coordinate (x,y)",
    tip: "Aggiungi nodo in una posizione",
  },
  {
    label: "Collega modulo1→modulo2",
    text: "Nel device (id) collega (modulo1) → (modulo2)",
    tip: "Crea connessione tra moduli",
  },
  {
    label: "Sposta modulo",
    text: "Nel device (id) sposta il modulo (label) a (x, y)",
    tip: "Sposta nodo",
  },
  {
    label: "Rimuovi modulo",
    text: "Nel device (id) rimuovi il modulo (label)",
    tip: "Rimuovi nodo",
  },
  {
    label: "Nuovo device ramificato",
    text: "Crea un nuovo device con MotherBoard collegata a SAT che è collegato a LTE/5G e LORa",
    tip: "Crea device ramificato",
  },
]);

function useSample(text: string, e: MouseEvent) {
  const isShift = e.shiftKey,
    isAlt = e.altKey || e.metaKey;
  prompt.value =
    isAlt && prompt.value.trim() ? `${prompt.value.trim()} ${text}` : text;
  if (isShift) sendPrompt();
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && show.value) {
    show.value = false;
    prompt.value = "";
  }
}
function closeButtonPrompt() {
  minimizeChat();
  prompt.value = "";
}

/* ---------- Greeting esterno ---------- */
const showGreeting = ref(true); // ad ogni accesso
const greetSide = ref<"left" | "right">("left");
function updateGreetSide() {
  const x = Number((pos as any)?.x ?? 0);
  greetSide.value = x < 220 ? "right" : "left";
}

/* === Username da /auth/me (con cache) === */
const WHO_AM_I = "/auth/me";
const nameRef = ref("");

const greetingText = computed(
  () => `Ciao${nameRef.value ? " " + nameRef.value : ""}, come posso aiutarti?`
);

async function resolveDisplayName() {
  // 1) Prop esplicita
  const fromProp = (props.userName || "").trim();
  if (fromProp) {
    nameRef.value = fromProp;
    return;
  }

  // 2) Cache sessione
  const cached = sessionStorage.getItem("auth.me");
  if (cached) {
    try {
      const d = JSON.parse(cached);
      const nm =
        d?.username ||
        d?.name ||
        (d?.email ? String(d.email).split("@")[0] : "");
      if (nm) {
        nameRef.value = nm;
        return;
      }
    } catch {}
  }

  // 3) Chiamata al BE
  try {
    const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
    const nm =
      data?.username ||
      data?.name ||
      (data?.email ? String(data.email).split("@")[0] : "");
    nameRef.value = nm || "";
    // salva una cache minimale
    sessionStorage.setItem(
      "auth.me",
      JSON.stringify({ username: nameRef.value, email: data?.email || null })
    );
  } catch {
    nameRef.value = ""; // fallback: nessun nome
  }
}

/* Chiudi fumetto quando apri la chat */
watch(show, (v) => {
  if (v) showGreeting.value = false;
});
/* Aggiorna lato quando la posizione cambia */
watch(
  () => [pos.x, pos.y],
  () => updateGreetSide()
);
/* Aggiorna nome se cambia la prop */
watch(
  () => props.userName,
  () => resolveDisplayName()
);

/* ---------- Flip sopra/sotto box ---------- */
const dropBelow = ref(false);
watch(show, async (v) => {
  if (!v) return;
  dropBelow.value = false;
  await nextTick();
  const box = promptBoxRef.value;
  if (!box) return;
  const rect = box.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight) {
    dropBelow.value = true;
    await nextTick();
  }
});

/* ---------- Route / Resize ---------- */
watch(
  () => route.name,
  () => {
    show.value = false;
    showGreeting.value = true; // ri-mostra ad ogni rientro pagina
    if (PERSIST_PER_ROUTE) loadPos();
    else resetPos();
    updateGreetSide();
    // non richiamiamo /auth/me ad ogni pagina: la cache è sufficiente.
  }
);

function onResize() {
  pos.x = clamp(pos.x, MARGIN, viewportMaxX());
  pos.y = clamp(pos.y, MARGIN, viewportMaxY());
  if (PERSIST_PER_ROUTE) savePos();
  updateGreetSide();
}

/* ---------- Drag handlers ---------- */
function onPointerDown(e: PointerEvent) {
  if (!fabRef.value) return;
  e.preventDefault();
  (fabRef.value as any).setPointerCapture?.(e.pointerId);
  isDragging.value = true;
  startPointer = { x: e.clientX, y: e.clientY };
  startPos = { x: pos.x, y: pos.y };
  movedDistance = 0;
}
function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - startPointer.x;
  const dy = e.clientY - startPointer.y;
  movedDistance = Math.max(movedDistance, Math.hypot(dx, dy));
  pos.x = clamp(startPos.x + dx, MARGIN, viewportMaxX());
  pos.y = clamp(startPos.y + dy, MARGIN, viewportMaxY());
}
function onPointerUp(e: PointerEvent) {
  if (!fabRef.value) return;
  (fabRef.value as any).releasePointerCapture?.(e.pointerId);
  const wasDragging = isDragging.value;
  isDragging.value = false;
  if (PERSIST_PER_ROUTE) savePos();
  if (movedDistance <= CLICK_TOLERANCE && wasDragging) {
    show.value = !show.value;
  }
}
function onPointerCancel() {
  isDragging.value = false;
}

/* ---------- Redirect + sendPrompt (immutato) ---------- */
function waitForEditor(timeout = 4000) {
  return new Promise<void>((resolve) => {
    if ((window as any).__editor_ready) return resolve();
    const t = setTimeout(resolve, timeout);
    const onReady = () => {
      clearTimeout(t);
      window.removeEventListener("editor-ready", onReady as EventListener);
      resolve();
    };
    window.addEventListener("editor-ready", onReady as EventListener, {
      once: true,
    });
  });
}
async function handleRedirect(
  redirect?: { name?: string; params?: Record<string, any> } | null
) {
  if (!redirect?.name) return;
  if (redirect.name === "Editor") {
    const deviceId = redirect.params?.deviceId ?? route.params.deviceId;
    await router.push({
      name: "Editor",
      params: deviceId ? { deviceId: String(deviceId) } : {},
      query: { skipDefaults: "1" },
    });
    return;
  }
  if (["Dashboard", "Devices", "Data", "Account"].includes(redirect.name)) {
    await router.push({
      name: redirect.name as any,
      params: redirect.params ?? {},
    });
    return;
  }
  await router.push({
    name: redirect.name as any,
    params: redirect.params ?? {},
  });
}
/* 
	manca logica per eliminare tutte le connessioni 
	manca logica per far eliminare anche tutti i nodi	X
	quando elimino un nodo non elimina automaticamente la connessione che quel nodo ha con un eventuale altro X
	fare in modo che nella chat lei già sappia a quale device ci stiamo riferendo
*/
// async function sendPrompt() {
//   if (!prompt.value.trim()) return;
//   try {
//     console.log("prompt", prompt.value);
//     const { data: ai } = await axios.post("/openai/request-action", {
//       prompt: prompt.value.trim(),
//     });
//     console.log("ai", ai);
//     const ci =
//       ai?.clientInstructions ??
//       (ai?.context
//         ? {
//             context: ai.context ?? null,
//             intent: ai.intent ?? "other",
//             params: ai.params ?? {},
//             missing: Array.isArray(ai?.missing) ? ai.missing : [],
//           }
//         : null);
//     if (ai?.ask || (ci?.missing && ci.missing.length)) {
//       toast.info(ai?.ask || `Mi servono altri dati: ${ci.missing.join(", ")}`, {
//         position: "top-right",
//         duration: 2500,
//       });
//       return;
//     }
//     if (ai?.redirect) await handleRedirect(ai.redirect);
//     if (ci) {
//       if (ci.context === "Editor") {
//         if (!ci.params?.deviceId && route.params.deviceId)
//           ci.params = { ...ci.params, deviceId: String(route.params.deviceId) };
//         if (ci.intent === "addNewDevice") {
//           const spec = ci.params?.graph;
//           const name =
//             ci.params?.name ?? `AI Device ${new Date().toLocaleString()}`;
//           if (!spec?.nodes?.length) {
//             toast.error("Nessun grafo da creare: mancano i nodi.", {
//               position: "top-right",
//             });
//             return;
//           }
//           const { data: device } = await axios.post("/devices/new-device");
//           const deviceId = device.id;
//           await router.push({
//             name: "Editor",
//             params: { deviceId: String(deviceId) },
//             query: { skipDefaults: "1" },
//           });
//           await waitForEditor();
//           await nextTick();
//           window.dispatchEvent(
//             new CustomEvent("ai-editor-refresh", {
//               detail: { deviceId, graphSpec: spec, name },
//             })
//           );

//           ok(successByIntent.addNewDevice);
//           show.value = false;
//           prompt.value = "";
//           return;
//         }

//         const deviceId = ci.params?.deviceId;
//         if (!deviceId) {
//           toast.error("Serve un deviceId (es. 'sul device 27 …').", {
//             position: "top-right",
//           });
//           return;
//         }
//         await router.push({
//           name: "Editor",
//           params: { deviceId: String(deviceId) },
//           query: { skipDefaults: "1" },
//         });
//         await waitForEditor();
//         await nextTick();
//         window.dispatchEvent(
//           new CustomEvent("ai-editor-command", { detail: ci })
//         );

//         const intentKey = String(ci.intent || "other"); // es. "addNewNode", "createConnection"…
//         ok(successByIntent[intentKey] ?? successByIntent.other);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Dashboard") {
//         await router.push({ name: "Dashboard" });

//         ok(successByIntent.dashboard);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Devices") {
//         await router.push({ name: "Devices" });

//         ok(successByIntent.devices);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Data") {
//         const deviceId = ci.params?.deviceId;
//         await router.push(
//           deviceId
//             ? { name: "Data", params: { deviceId: String(deviceId) } }
//             : { name: "Data" }
//         );

//         ok(successByIntent.data);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Account") {
//         await router.push({ name: "Account" });

//         ok(successByIntent.account);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//     }
//     window.dispatchEvent(new CustomEvent("ai-response", { detail: ai }));
//   } catch (e) {
//     console.error("Errore nella richiesta AI:", e);
//     toast.error("Errore richiesta AI", { position: "top-right" });
//   }
// }

// ——— helpers locali ———
function toOps(ai: any) {
  if (Array.isArray(ai?.operations) && ai.operations.length)
    return ai.operations;
  if (ai?.clientInstructions) return [ai.clientInstructions];
  if (ai?.context) {
    return [
      {
        context: ai.context ?? null,
        intent: ai.intent ?? "other",
        params: ai.params ?? {},
        missing: Array.isArray(ai?.missing) ? ai.missing : [],
      },
    ];
  }
  return [];
}

function ensureDeviceIdOnOps(ops: any[], fallbackId?: string | number) {
  const devId = fallbackId != null ? String(fallbackId) : undefined;
  return ops.map((op) => {
    if (op?.context === "Editor") {
      const p = op.params ?? {};
      if (!p.deviceId && devId) op.params = { ...p, deviceId: devId };
    }
    return op;
  });
}

// ——— DROP-IN sendPrompt ———
async function sendPrompt() {
  if (!prompt.value.trim()) return;
  const currentEditorDeviceId =
    route.name === "Editor" && route.params.deviceId
      ? String(route.params.deviceId)
      : undefined;

  try {
    // [NEW] Build prompt with map context if available
    const mapCtx = hasContext.value ? getContextForAI() : '';
    const fullPrompt = mapCtx 
      ? `${mapCtx}\n\nDOMANDA UTENTE: ${prompt.value.trim()}`
      : prompt.value.trim();
    
    const { data: ai } = await axios.post("/openai/request-action", {
      prompt: fullPrompt,
      context: { currentEditorDeviceId, hasMapContext: hasContext.value },
    });
    console.log("prompt", prompt);
    console.log("risposta ai", ai);
    // 1) chiede slot mancanti / domande follow-up
    const ci =
      ai?.clientInstructions ??
      (ai?.context
        ? {
            context: ai.context ?? null,
            intent: ai.intent ?? "other",
            params: ai.params ?? {},
            missing: Array.isArray(ai?.missing) ? ai.missing : [],
          }
        : null);

    if (ai?.ask || (ci?.missing && ci.missing.length)) {
      toast.info(ai?.ask || `Mi servono altri dati: ${ci.missing.join(", ")}`, {
        position: "top-right",
        duration: 2500,
      });
      return;
    }

    // 2) redirect (se presente)
    if (ai?.redirect) await handleRedirect(ai.redirect);

    // 3) normalizza in lista di operazioni
    let ops = toOps(ai);
    // fallback: se non arriva nulla ma avevamo la forma "ci", trattala come singola op
    if (!ops.length && ci) ops = [ci];

    // 3.a aggiungi deviceId mancante dalle route params
    ops = ensureDeviceIdOnOps(ops, route.params.deviceId as string | undefined);

    // 4) Se tra le ops c’è un addNewDevice con graph, gestiscilo qui
    //    (crea il device, carica editor, applica graph via evento "ai-editor-refresh")
    for (const op of ops) {
      if (op?.context === "Editor" && op?.intent === "addNewDevice") {
        const spec = op.params?.graph;
        const name =
          op.params?.name ?? `AI Device ${new Date().toLocaleString()}`;

        if (!spec?.nodes?.length) {
          toast.error("Nessun grafo da creare: mancano i nodi.", {
            position: "top-right",
          });
          return;
        }

        const { data: device } = await axios.post("/devices/new-device");
        const deviceId = device.id;
        await router.push({
          name: "Editor",
          params: { deviceId: String(deviceId) },
          query: { skipDefaults: "1" },
        });
        await waitForEditor();
        await nextTick();
        window.dispatchEvent(
          new CustomEvent("ai-editor-refresh", {
            detail: { deviceId, graphSpec: spec, name },
          })
        );

        ok("Device creato con successo");
        show.value = false;
        prompt.value = "";
        return; // le altre ops (se esistono) non si applicano allo stesso addNewDevice
      }
    }

    // 5) Per tutte le altre operazioni Editor (addNewNode / removeNode / createConnection / moveNode):
    //    invia un unico payload con operations al listener dell’Editor (batch)
    const editorOps = ops.filter((op: any) => op?.context === "Editor");
    const nonEditorOps = ops.filter(
      (op: any) => op?.context && op.context !== "Editor"
    );

    // esegui eventuali redirect “non Editor” (Dashboard/Data/Devices/Account)
    for (const op of nonEditorOps) {
      const name = op.context;
      if (name === "Dashboard") {
        await router.push({ name: "Dashboard" });
        ok("Ecco la dashboard");
      } else if (name === "Devices") {
        await router.push({ name: "Devices" });
        ok("Ecco la lista dispositivi");
      } else if (name === "Data") {
        const deviceId = op.params?.deviceId;
        await router.push(
          deviceId
            ? { name: "Data", params: { deviceId: String(deviceId) } }
            : { name: "Data" }
        );
        ok("Ecco la sezione dati");
      } else if (name === "Account") {
        await router.push({ name: "Account" });
        ok("Ecco la pagina account");
      }
    }

    if (editorOps.length) {
      // prendi un deviceId “di riferimento” dalla prima op Editor
      const firstDevId = editorOps.find((o: any) => o?.params?.deviceId)?.params
        ?.deviceId;
      if (!firstDevId) {
        toast.error("Serve un deviceId (es. 'sul device 27 …').", {
          position: "top-right",
        });
        return;
      }
      await router.push({
        name: "Editor",
        params: { deviceId: String(firstDevId) },
        query: { skipDefaults: "1" },
      });
      await waitForEditor();
      await nextTick();

      // invia TUTTE le operazioni in un colpo solo (batch)
      window.dispatchEvent(
        new CustomEvent("ai-editor-command", {
          detail: { operations: editorOps },
        })
      );

      // feedback ottimistico
      const intents = new Set<string>(
        editorOps.map((o: any) => String(o.intent || "other"))
      );
      // scegli il messaggio migliore se c’è una sola intenzione, altrimenti “Operazioni eseguite”
      if (intents.size === 1) {
        const k = intents.values().next().value as string;
        ok(successByIntent[k] ?? successByIntent.other);
      } else {
        ok("Operazioni eseguite");
      }
    }

    // 6) chiudi UI
    show.value = false;
    prompt.value = "";
    // facoltativo: emit debug completo
    window.dispatchEvent(new CustomEvent("ai-response", { detail: ai }));
  } catch (e) {
    console.error("Errore nella richiesta AI:", e);
    toast.error("Errore richiesta AI", { position: "top-right" });
  }
}

/* ---------- lifecycle ---------- */
onMounted(() => {
  if (PERSIST_PER_ROUTE) loadPos();
  else resetPos();
  updateGreetSide();
  showGreeting.value = true; // ad ogni accesso
  resolveDisplayName(); // ⇐ prende il nome da /auth/me (con cache)

  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
.ai-floating-chat {
  --fab-size: 56px;
  position: fixed;
  z-index: 100000;
  width: var(--fab-size);
  height: var(--fab-size);
}
/* Greeting bubble */
.greeting-bubble {
  position: absolute;
  bottom: 8px;
  right: calc(var(--fab-size) + 12px);
  max-width: 320px;
  padding: 12px 14px;
  background: #008f8f;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  font: 12px/1.4 "Inter", sans-serif;
  color: #d0f2f2;
}
.greeting-bubble p {
  margin: 0;
}
.greeting-bubble.right {
  right: auto;
  left: calc(var(--fab-size) + 12px);
}
.greeting-bubble::after {
  content: "";
  position: absolute;
  bottom: 16px;
  right: -8px;
  border-width: 8px;
  border-style: solid;
  border-color: transparent transparent transparent #008f8f;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.08));
}
.greeting-bubble.right::after {
  left: -8px;
  right: auto;
  border-color: transparent #008f8f transparent transparent;
}
.ai-greet-enter-active,
.ai-greet-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.ai-greet-enter-from,
.ai-greet-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

/* FAB Container & Close */
.fab-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fab-close-btn {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 22px;
  height: 22px;
  background: #ff4d4d;
  border: 2px solid #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  z-index: 10;
}
.fab-container:hover .fab-close-btn {
  opacity: 1;
}
.fab-close-btn:hover {
  transform: scale(1.1);
  background: #ff3333;
}
.fab-close-btn svg {
  width: 12px;
  height: 12px;
  stroke: #fff;
}

/* FAB */
.ai-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  /* opzionale: variabile per l'icona */
  --icon-size: calc(
    var(--fab-size) * 0.58
  ); /* ~58% del bottone, regola a piacere */
  background: #008f8f;
  color: #fff;
  width: var(--fab-size);
  height: var(--fab-size);
  border-radius: 50%;
  border: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  cursor: grab;
  touch-action: none;
  user-select: none;
}

/* riduci l’img del logo dentro il FAB */
.ai-toggle-btn img {
  width: var(--icon-size);
  height: var(--icon-size);
  object-fit: contain;
  display: block;
  pointer-events: none; /* il drag rimane sul bottone, non sull'img */
}

.ai-toggle-btn.dragging {
  cursor: grabbing;
}
/* Prompt box */
.ai-prompt-box {
  position: absolute;
  right: 0;
  width: 420px;
  max-width: 90vw;
  padding: 12px;
  padding-top: 36px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.25);
}
.ai-prompt-box.above {
  bottom: calc(var(--fab-size) + 12px);
}
.ai-prompt-box.below {
  top: calc(var(--fab-size) + 12px);
}
.ai-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: 0;
  padding: 4px;
  cursor: pointer;
  line-height: 0;
}
.ai-close-btn svg {
  width: 18px;
  height: 18px;
  stroke: #000;
  transition: stroke 0.2s;
}
.ai-close-btn:hover svg,
.ai-close-btn:focus-visible svg {
  stroke: #008f8f;
}
.ai-prompt-box textarea {
  width: 100%;
  height: 100px;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  font: 14px/1.3 "Inter", sans-serif;
  resize: vertical;
}
.chips {
  margin: 8px 0 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 110px;
  overflow-y: auto;
}
.chips-label {
  flex-basis: 100%;
  font-size: 12px;
  color: #666;
  margin-bottom: -2px;
}
.chip {
  border: 1px solid #e3e8ef;
  background: #f7fafc;
  color: #0f172a;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.06s;
  white-space: nowrap;
}
.chip:hover {
  background: #eef2f7;
  border-color: #d7dee7;
}
.chip:active {
  transform: scale(0.98);
}
.send-btn {
  width: 100%;
  margin-top: 4px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: #008f8f;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.send-btn:hover {
  background: #00a7a7;
}
/* mobile */
/* --- MOBILE --- */
@media (max-width: 768px) {
  /* FUMETTO: sopra al FAB e non cliccabile (già fatto) */
  .greeting-bubble,
  .greeting-bubble.right {
    left: auto !important;
    right: 0 !important;
    bottom: calc(var(--fab-size) + 12px) !important;
    transform: none !important;
    max-width: min(320px, calc(100vw - 24px));
  }
  .greeting-bubble {
    pointer-events: none;
  }
  .greeting-bubble::after,
  .greeting-bubble.right::after {
    display: none;
  }

  /* PROMPT BOX: usa il viewport, non il wrapper del FAB */
  .ai-prompt-box {
    position: fixed; /* <— da absolute a fixed */
    left: 8px;
    right: 8px;
    width: auto; /* niente max-width legato al wrapper */
    max-width: none;
    transform: none !important; /* annulla translateX(-50%) */
    margin: 0;
    z-index: 100001; /* sopra al FAB */
    max-height: calc(
      100vh - 32px - var(--fab-size)
    ); /* evita tagli verticali */
    overflow: auto;
  }
  .ai-prompt-box.above {
    bottom: calc(env(safe-area-inset-bottom, 0px) + var(--fab-size) + 16px);
    top: auto;
  }
  .ai-prompt-box.below {
    top: calc(env(safe-area-inset-top, 0px) + var(--fab-size) + 16px);
    bottom: auto;
  }
}

/* Tab Style */
.ai-chat-tab {
  position: fixed;
  bottom: 0;
  right: 20px;
  background: #008f8f;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s, background 0.2s;
  z-index: 99999;
}
.ai-chat-tab:hover {
  transform: translateY(-2px);
  background: #00a7a7;
}
.tab-icon img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.ai-chat-tab span {
  font-size: 14px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
}
</style>
